/**
 * Labelling: how much of the organisation's content carries a sensitivity label (GET /admin/v1/label-report). The data
 * column (Layout.tsx): the period and the business area in the header, a note on where Purview stands, four numbers
 * (coverage, labelled memory, unlabelled sessions, unclassified projects), then the parts: by label, sessions, over time,
 * by project and by business area. Each project opens its memory.
 *
 * It reads AIOE's own four levels, so it is as useful with no Microsoft Purview connection as with one. Purview adds
 * the label names machines met in sessions. Memory is what is held now; sessions and the series are the period.
 */

import { SENSITIVITIES, type LabelReport, type LabelReportAreaRow, type LabelReportProjectRow } from '@aioe/shared';
import { Briefcase, ChartColumn, Database, Folder, Pulse, Shield, Tag, TriangleExclamation } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, routes } from '../api/client.ts';
import { TrendChart, type TrendSeries } from '../components/costs/EconomicsCharts.tsx';
import { LevelBar, LevelLegend, LEVEL_WORDS, n } from '../components/labelling/LabellingBits.tsx';
import { ArtEmpty, ArtSection, ChoiceSelect, ErrorNote, ListBlock, PageColumn, PageHeader, Stat } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { useLoad } from '../hooks.ts';
import '../components/labelling/labelling.css';
import '../components/costs/economics.css';

type Range = '7' | '30' | '90';
const RANGES: Record<Range, string> = { '7': '7 days', '30': '30 days', '90': '90 days' };
const ALL = '__all';
const SHOWN = 25;
type Trend = 'sessions' | 'memory' | 'relabels';
type Day = LabelReport['series'][number] & { at: string };

const tone = VIEW_TONES.labelling!;
const percent = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');
const forbidden = (e: unknown) => e instanceof ApiError && e.status === 403;

export function LabellingPage() {
  const [range, setRange] = useState<Range>('30');
  const [area, setArea] = useState<string>(ALL);
  const areas = useLoad(() => routes.areas().catch(() => []), []);
  const report = useLoad(
    () => routes.labelReport({ days: Number(range), ...(area !== ALL ? { businessAreaId: area } : {}) }).catch((e: unknown) => (forbidden(e) ? null : Promise.reject(e))),
    [range, area],
    300_000,
    { whenVisible: true },
  );
  const areaOptions = useMemo(() => ({ [ALL]: 'All business areas', ...Object.fromEntries((areas.data ?? []).map((a) => [a.id, a.name])) }), [areas.data]);
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Tag />}
        title="Labelling"
        subtitle="How much of the organisation’s content carries a sensitivity label: memory, sessions, projects and portfolios."
        actions={
          <>
            <Segment aria-label="Period" size="sm" selectedKey={range} onSelectionChange={(k) => setRange(String(k) as Range)}>
              {(Object.keys(RANGES) as Range[]).map((r) => (
                <Segment.Item key={r} id={r}>
                  {RANGES[r]}
                </Segment.Item>
              ))}
            </Segment>
            {(areas.data?.length ?? 0) > 0 ? <ChoiceSelect label="Business area" value={area} options={areaOptions} onChange={setArea} /> : null}
          </>
        }
      />
      <Explainer id="labelling" tone={tone} icon={<Tag />} title="How labelling is counted" summary="Four levels, always known; Purview adds the names of its own labels">
        <p>Every memory item carries one of four levels: public, internal, confidential or restricted. An item with no label takes its project’s default, so memory is never unlabelled; the report says who set each level instead. A project carries a classification when an owner sets one, otherwise it is unclassified.</p>
        <p>A session counts as labelled when it met labelled content or wrote to memory at a level during the period, and takes the highest level it met. A session that met nothing labelled is unlabelled, which says no label was seen, not that nothing sensitive happened.</p>
        <p>Coverage is the share of sessions and projects that carry a label. The figures count; they never show content.</p>
      </Explainer>
      <PartBoundary name="Labelling report" tone={tone}>
        {report.data === null ? (
          <ArtEmpty tone={tone} icon={<Tag />} title="Labelling is for administrators">
            Administrators, and anyone given the organisation’s integrations, read how much content is labelled.
          </ArtEmpty>
        ) : !report.data ? (
          report.error ? <ErrorNote error={report.error} /> : <p className="text-sm text-muted">Reading the labels…</p>
        ) : (
          <Report r={report.data} area={area === ALL ? undefined : (areas.data ?? []).find((a) => a.id === area)?.name} />
        )}
      </PartBoundary>
    </PageColumn>
  );
}

function Report({ r, area }: { r: LabelReport; area?: string }) {
  const [trend, setTrend] = useState<Trend>('sessions');
  const [all, setAll] = useState(false);
  const nothing = r.memory.total === 0 && r.sessions.total === 0 && r.projects.total === 0 && r.organisationMemory.total === 0;
  const unlabelled = r.sessions.unlabelled + r.projects.unclassified;
  const points: Day[] = useMemo(() => r.series.map((d) => ({ ...d, at: `${d.day}T00:00:00.000Z` })), [r.series]);
  const series: TrendSeries<Day>[] =
    trend === 'sessions'
      ? [
          { label: 'Sessions', colour: 'var(--series-2)', value: (p) => p.sessions },
          { label: 'Labelled sessions', colour: 'var(--series-1)', value: (p) => p.labelledSessions },
        ]
      : trend === 'memory'
        ? SENSITIVITIES.map((l, i) => ({ label: LEVEL_WORDS[l], colour: `var(--series-${i + 1})`, value: (p: Day) => p.memoryByLevel[l] }))
        : [{ label: 'Relabelled by a person', colour: 'var(--series-1)', value: (p) => p.relabels }];
  const projects = all ? r.byProject : r.byProject.slice(0, SHOWN);
  if (nothing)
    return (
      <>
        <PurviewNote r={r} />
        <ArtEmpty tone={tone} icon={<Tag />} title={area ? `Nothing to count in ${area}` : 'Nothing to count yet'}>
          This fills as projects register, workbenches push memory and agents run sessions. Memory is counted by its level, sessions by the labels they met, and projects by their classification.
        </ArtEmpty>
      </>
    );
  return (
    <div className="lbl econ">
      <PurviewNote r={r} />
      <div className="art-stats">
        <Stat tone={tone} icon={<Shield />} label="Coverage" value={`${r.coverage.percent.toLocaleString('en-AU', { maximumFractionDigits: 1 })}%`} detail={`${n(r.coverage.labelled)} of ${n(r.coverage.labelled + r.coverage.unlabelled)} sessions and projects`} />
        <Stat tone={tone} icon={<Database />} label="Labelled memory" value={r.memory.total} detail={r.organisationMemory.total ? `And ${n(r.organisationMemory.total)} in the organisation’s own` : 'Every item carries a level'} />
        <Stat tone={tone} icon={<Pulse />} label="Unlabelled sessions" value={r.sessions.unlabelled} detail={r.sessions.total ? `Of ${n(r.sessions.total)} in the period` : 'No sessions in the period'} needsYou={r.sessions.unlabelled > 0} />
        <Stat tone={tone} icon={<TriangleExclamation />} label="Unclassified projects" value={r.projects.unclassified} detail={`Of ${n(r.projects.total)} projects`} needsYou={r.projects.unclassified > 0} />
      </div>

      <ArtSection tone={tone} icon={<Tag />} title="By label" hint={area ? `${area}. Memory is what is held now` : 'Memory is what is held now'}>
        <LevelLegend />
        <ul className="lbl-rows">
          <li className="lbl-row">
            <span className="lbl-row__name">Memory</span>
            <span className="lbl-row__bar">
              <LevelBar counts={r.memory.byLevel} label="Memory items" />
            </span>
            <span className="lbl-row__total">{n(r.memory.total)} items</span>
          </li>
          <li className="lbl-row">
            <span className="lbl-row__name">Sessions</span>
            <span className="lbl-row__bar">
              <LevelBar counts={r.sessions.byLevel} label="Labelled sessions, by the highest level met" />
            </span>
            <span className="lbl-row__total">{n(r.sessions.labelled)} labelled</span>
            <span className="lbl-row__note">By the highest level each met. {n(r.sessions.unlabelled)} met no label.</span>
          </li>
          <li className="lbl-row">
            <span className="lbl-row__name">Projects</span>
            <span className="lbl-row__bar">
              <LevelBar counts={r.projects.byClassification} label="Classified projects" />
            </span>
            <span className="lbl-row__total">{n(r.projects.classified)} classified</span>
            <span className="lbl-row__note">{n(r.projects.unclassified)} not classified yet.</span>
          </li>
        </ul>
        <div className="lbl__split mt-5">
          <div>
            <h3 className="lbl__sub">Who set a memory level</h3>
            <ul className="lbl__list">
              <li>
                <span>Labelling rules and the project’s default</span>
                <b>{n(r.memory.byOrigin.rulesOrDefault)}</b>
              </li>
              <li>
                <span>An agent’s write</span>
                <b>{n(r.memory.byOrigin.agent)}</b>
              </li>
              <li>
                <span>A person, by relabelling</span>
                <b>{n(r.memory.byOrigin.person)}</b>
              </li>
              <li>
                <span>Also in a compartment</span>
                <b>{n(r.memory.withCompartments)}</b>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="lbl__sub">Microsoft Purview labels met in sessions</h3>
            {r.sessions.purviewLabels.length ? (
              <ul className="lbl__list">
                {r.sessions.purviewLabels.map((l) => (
                  <li key={l.name}>
                    <span>
                      {l.name}
                      {l.sensitivity ? <span className="text-muted"> · {LEVEL_WORDS[l.sensitivity]}</span> : null}
                    </span>
                    <b title={`${n(l.events)} times`}>{n(l.sessions)} {l.sessions === 1 ? 'session' : 'sessions'}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="lbl__muted">{r.purview.connected ? 'No session named a Purview label in this period.' : 'Purview is not connected, so no label names are known. Levels above are the organisation’s own.'}</p>
            )}
          </div>
        </div>
      </ArtSection>

      <ArtSection tone={tone} icon={<ChartColumn />} title="Over time" hint={`Per day, ${r.window.days} days`} actions={
        <Segment aria-label="Show" size="sm" selectedKey={trend} onSelectionChange={(k) => setTrend(String(k) as Trend)}>
          <Segment.Item id="sessions">Sessions</Segment.Item>
          <Segment.Item id="memory">Memory written</Segment.Item>
          <Segment.Item id="relabels">Relabelled</Segment.Item>
        </Segment>
      }>
        <TrendChart points={points} series={series} bucket="day" format={n} label={trend === 'sessions' ? 'Sessions' : trend === 'memory' ? 'Memory items written, by level' : 'Memory relabelled by a person'} />
      </ArtSection>

      <ArtSection tone={tone} icon={<Folder />} title="By project" count={r.projects.total} flush hint={r.truncated ? `The largest ${n(r.byProject.length)}` : undefined}>
        {projects.length === 0 ? (
          <ListBlock>No projects match.</ListBlock>
        ) : (
          <>
            <ProjectTable rows={projects} />
            {r.byProject.length > SHOWN ? (
              <div className="lbl__more">
                <button type="button" className="text-sm text-accent hover:underline" onClick={() => setAll(!all)}>
                  {all ? 'Show fewer' : `Show all ${n(r.byProject.length)}`}
                </button>
              </div>
            ) : null}
          </>
        )}
      </ArtSection>

      <ArtSection tone={tone} icon={<Briefcase />} title="By business area" count={r.byBusinessArea.length} flush>
        {r.byBusinessArea.length === 0 ? <ListBlock>No business areas yet.</ListBlock> : <AreaTable rows={r.byBusinessArea} />}
      </ArtSection>
      <p className="text-xs text-muted">Counted {new Date(r.generatedAt).toLocaleString('en-AU')}. {unlabelled > 0 ? `${n(unlabelled)} sessions and projects carry no label.` : 'Every session and project carries a label.'}</p>
    </div>
  );
}

function PurviewNote({ r }: { r: LabelReport }) {
  return (
    <p className="lbl__muted" role="note">
      {r.purview.connected ? (
        <>
          Microsoft Purview is connected: {n(r.purview.labels)} labels, {n(r.purview.mapped)} mapped to a level. Counts below use the organisation’s own four levels; Purview’s label names show beside the sessions that met them.{' '}
          <Link to="/settings/purview" className="text-accent hover:underline">
            Label mapping
          </Link>
        </>
      ) : (
        <>
          Microsoft Purview is not connected, so this counts the organisation’s own four levels. Connect it to see Purview’s label names beside the sessions that met them.{' '}
          <Link to="/settings/purview" className="text-accent hover:underline">
            Connect Purview
          </Link>
        </>
      )}
    </p>
  );
}

const memoryLink = (id: string) => `/projects/${encodeURIComponent(id)}?tab=memory`;

function ProjectTable({ rows }: { rows: LabelReportProjectRow[] }) {
  return (
    <div className="data-table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>Project</th>
            <th className="hidden w-44 @3xl:table-cell">Business area</th>
            <th className="hidden w-36 @2xl:table-cell">Classification</th>
            <th className="w-36 @2xl:w-56">Memory</th>
            <th className="hidden w-40 @4xl:table-cell">Sessions labelled</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.projectId}>
              <td>
                <Link to={memoryLink(p.projectId)} className="block max-w-full truncate font-medium hover:underline" title="Open this project’s memory">
                  {p.name}
                </Link>
                <span className="data-table__meta @2xl:hidden">{p.classification ? LEVEL_WORDS[p.classification] : 'Unclassified'}</span>
              </td>
              <td className="hidden @3xl:table-cell">{p.businessAreaName ?? <span className="text-muted">None</span>}</td>
              <td className="hidden @2xl:table-cell">{p.classification ? LEVEL_WORDS[p.classification] : <span className="text-muted">Unclassified</span>}</td>
              <td>
                <div className="lbl__mix">
                  <LevelBar small counts={p.memoryByLevel} label={`${p.name} memory`} />
                  <span>{n(p.memoryItems)} items</span>
                </div>
              </td>
              <td className="hidden data-table__end @4xl:table-cell">{p.sessions ? `${n(p.labelledSessions)} of ${n(p.sessions)} (${percent(p.labelledSessions, p.sessions)})` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AreaTable({ rows }: { rows: LabelReportAreaRow[] }) {
  return (
    <div className="data-table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>Business area</th>
            <th className="w-36 @2xl:w-44">Projects classified</th>
            <th className="w-36 @2xl:w-56">Memory</th>
            <th className="hidden w-32 @4xl:table-cell">Sessions labelled</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.businessAreaId ?? 'none'}>
              <td>
                <span className="block max-w-full font-medium">{a.name}</span>
                <span className="data-table__meta">{n(a.projects)} {a.projects === 1 ? 'project' : 'projects'}</span>
              </td>
              <td>
                <div className="lbl__mix">
                  <LevelBar small counts={a.classificationByLevel} label={`${a.name} classification`} />
                  <span>
                    {n(a.classified)} of {n(a.projects)}
                  </span>
                </div>
              </td>
              <td>
                <div className="lbl__mix">
                  <LevelBar small counts={a.memoryByLevel} label={`${a.name} memory`} />
                  <span>{n(a.memoryItems)} items</span>
                </div>
              </td>
              <td className="hidden data-table__end @4xl:table-cell">{a.sessions ? `${n(a.labelledSessions)} of ${n(a.sessions)} (${percent(a.labelledSessions, a.sessions)})` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
